import {z} from 'zod'

import type {JobLocation} from './types'

/**
 * One stop, as every write path sends it: project-job creation, service-schedule creation, and job
 * location reconcile. The three paths share this shape so a location cannot mean different things
 * depending on which wizard produced it.
 */
const locationInputFields = z.object({
    name: z.string(),
    latitude: z.number(),
    longitude: z.number(),
    notes: z.string().nullable().optional(),
    fullAddress: z.string().nullable().optional(),
    fromSavedLocationId: z.string().nullable().optional(),
    rememberAsSavedLocation: z.boolean().optional(),
})

export const locationInputSchema = locationInputFields

export type LocationInput = z.infer<typeof locationInputSchema>

/** A stop on a reconcile request, where an existing stop carries its `job_location` id. */
export type ReconcileLocationInput = LocationInput & {id?: string}

/** Map one location-editor row onto a request input. */
export function jobLocationToInput(location: JobLocation & {id?: string}): ReconcileLocationInput {
    return {
        ...(location.id ? {id: location.id} : {}),
        name: location.name.trim(),
        latitude: location.latitude!,
        longitude: location.longitude!,
        notes: location.notes.trim() || null,
        fullAddress:
            location.positionMode === 'address' && location.addressText?.trim()
                ? location.addressText.trim()
                : undefined,
        fromSavedLocationId: location.fromSavedLocationId,
        rememberAsSavedLocation: location.rememberAsSavedLocation,
    }
}

/** Map the location-editor rows that have coordinates onto request inputs. */
export function jobLocationsToInputs(locations: JobLocation[]): LocationInput[] {
    return locations
        .filter((location) => location.latitude != null && location.longitude != null)
        .map((location) => jobLocationToInput(location))
}

/** Serialize one location input for the API. */
export function toLocationRequest(location: ReconcileLocationInput) {
    return {
        ...(location.id ? {id: location.id} : {}),
        name: location.name.trim(),
        latitude: location.latitude,
        longitude: location.longitude,
        notes: location.notes?.trim() || null,
        ...(location.fullAddress ? {full_address: location.fullAddress} : {}),
        ...(location.fromSavedLocationId !== undefined
            ? {from_saved_location_id: location.fromSavedLocationId}
            : {}),
        ...(location.rememberAsSavedLocation ? {remember_as_saved_location: true} : {}),
    }
}
